import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import toast from 'react-hot-toast';
import StatusPill from '../components/StatusPill';
import { Send, Users, CheckCircle2, AlertCircle } from 'lucide-react';

export default function SlotActivationPage() {
  const { t, i18n } = useTranslation();
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ starts_at: '', duration_min: 30, note: '', ttl_min: 30 });
  const [matchPreview, setMatchPreview] = useState(null);
  const [activating, setActivating] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/slots');
      setSlots(r.data.slots || []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!form.duration_min) return;
    api.get('/api/slots/preview-match', { params: { duration_min: form.duration_min } })
      .then((r) => setMatchPreview(r.data))
      .catch(() => setMatchPreview(null));
  }, [form.duration_min]);

  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'de-AT';
  const fmt = (d) => new Date(d).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });

  async function activate(e) {
    e.preventDefault();
    if (!form.starts_at) return;
    setActivating(true);
    try {
      const r = await api.post('/api/slots/activate', {
        starts_at: new Date(form.starts_at).toISOString(),
        duration_min: Number(form.duration_min) || 30,
        note: form.note,
        ttl_min: Number(form.ttl_min) || 30,
      });
      const sent = r.data.sent || 0;
      const pf = r.data.pull_forward;
      if (sent > 0) toast.success(`${sent} Patient*innen benachrichtigt.`);
      else if (pf?.sent > 0) toast.success(`Warteliste leer — ${pf.sent} Vorzieh-Angebote gesendet.`);
      else if (pf?.alert) toast.error('Max. Kettenreaktionen erreicht — bitte manuell prüfen.');
      else toast('Niemand in Warteliste / Vorziehen passt zur Dauer.');
      setForm({ starts_at: '', duration_min: 30, note: '', ttl_min: 30 });
      load();
    } catch (e) {
      toast.error(t('errors.unknown'));
    } finally { setActivating(false); }
  }

  return (
    <div data-testid="slot-activation-page">
      <h1 className="text-2xl font-bold tracking-tight">{t('slotActivation.title')}</h1>
      <p className="mt-1 text-sm text-text-secondary">{t('slotActivation.subtitle')}</p>

      <form onSubmit={activate} className="so-card mt-6 p-5 grid grid-cols-1 md:grid-cols-5 gap-3 items-end" data-testid="slot-form">
        <div>
          <label className="so-label">Datum &amp; Uhrzeit</label>
          <input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="so-input" required data-testid="slot-starts-at"/>
        </div>
        <div>
          <label className="so-label">Dauer (Min.)</label>
          <input type="number" min="5" max="480" step="5" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="so-input" data-testid="slot-duration"/>
        </div>
        <div>
          <label className="so-label">Gültigkeit (Min.)</label>
          <select value={form.ttl_min} onChange={(e) => setForm({ ...form, ttl_min: e.target.value })} className="so-input" data-testid="slot-ttl">
            <option value={15}>15 Minuten</option>
            <option value={30}>30 Minuten</option>
            <option value={60}>60 Minuten</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="so-label">Notiz (optional)</label>
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="so-input" data-testid="slot-note"/>
        </div>
        <div className="md:col-span-5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm" data-testid="slot-match-preview">
            <Users size={16} className="text-accent"/>
            {matchPreview != null && (
              <span>
                <strong className="text-text">{matchPreview.matching_count}</strong>{' '}
                <span className="text-text-secondary">passende Patient*innen für {form.duration_min} Min.</span>
              </span>
            )}
          </div>
          <button type="submit" disabled={activating || !form.starts_at} className="so-btn so-btn-primary text-base px-6 py-3" data-testid="slot-activate-btn">
            <Send size={16}/> Warteliste aktivieren
          </button>
        </div>
      </form>

      <div className="so-card mt-6 overflow-hidden">
        <div className="px-5 py-3 border-b border-border bg-background"><h2 className="text-sm font-semibold">Aktive &amp; vergangene Slots</h2></div>
        {slots.length === 0 ? (
          <div className="p-10 text-center text-text-secondary">{t('slotActivation.empty')}</div>
        ) : (
          <table className="so-table">
            <thead>
              <tr>
                <th>Datum &amp; Zeit</th>
                <th>Dauer</th>
                <th>Status</th>
                <th>Gefüllt von</th>
                <th>Quelle</th>
              </tr>
            </thead>
            <tbody>
              {slots.map((s) => (
                <tr key={s._id} data-testid={`slot-row-${s._id}`}>
                  <td className="font-mono text-xs whitespace-nowrap">{fmt(s.starts_at)}</td>
                  <td>{s.duration_min || '?'} Min</td>
                  <td><StatusPill status={s.status} label={t(`slotActivation.status.${s.status}`, s.status)}/></td>
                  <td className="font-mono text-xs">{s.filled_by_phone || s.filled_by_patient_phone || '—'}</td>
                  <td className="text-xs text-text-secondary">{s.source_appointment_id ? 'aus Absage' : 'manuell'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
